import React, { useCallback, memo, useRef, useState, useMemo, useImperativeHandle, ReactNode } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

import {
    Container,
    Slider,
    SwipeButton,
    IconContainer,
    Content,
    SubTextContainer,
    SubText,
    DiscountSubText,
} from "./styles/SlideButton.styled";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import CircleIcon from "./CircleIcon";
import { useMountedRef } from "@core-ui/hooks/UseMountedRef";
import { isTouchEvent } from "@core-ui/helpers/Dom";
import { BoltIcon } from "@core-ui/components/bolt-icon";
import { PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET } from "@core-ui/constants/SliderButton";

const SLIDE_THRESHOLD_PERCENT = 0.7;
const ICON_WIDTH = 48; // px
const CONTAINER_PADDING = 6; // px
const ANIMATION_DURATION = 800; // ms
const TOUCH_Y_THRESHOLD = 70; //px
export const DATA_TEST_IDS = {
    SLIDER: "turbo-slider",
};

export type SlideButtonProps = {
    dataTestId: string;
    onSuccess?: (sliderLeft: number, containerWidth: number) => void;
    onFailure?: (sliderLeft: number, containerWidth: number) => void;
    onDragUpdate?: (sliderLeft: number, containerWidth: number) => void;
    turboCtaText?: string;
    prefixText: string;
    amountText: string;
    discountSubtext?: string;
    successText?: string;
    containerClassName?: string;
    showInitialBumpAnimation?: boolean;
    showSubtextForInstantFoP?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sliderButtonRef: any;
    increaseSliderWidth?: boolean;
};

function SlideButton(props: SlideButtonProps) {
    const isTouchDevice = "ontouchstart" in document.documentElement;
    const sliderRef = useRef<HTMLButtonElement | null>(null);
    const sliderButtonRef = useRef<HTMLButtonElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const isMounted = useMountedRef();
    const [state, setState] = useState({ unlocked: false });
    const thisValue = useRef({ isDragging: false, startX: 0, startY: 0, sliderLeft: 0, containerWidth: 0 });
    const [showTapAnimation, setShowTapAnimation] = useState(false);

    const getCurrentSwipeProgress = useCallback((bufferValue = ICON_WIDTH) => {
        return thisValue.current.sliderLeft / (thisValue.current.containerWidth + bufferValue);
    }, []);

    // eslint-disable-next-line max-params
    const getValueAfterRangeConvert = useCallback((oldMin, oldMax, newMin, newMax, oldValue): number => {
        const oldRange = oldMax - oldMin;
        const newRange = newMax - newMin;
        return ((oldValue - oldMin) * newRange) / oldRange + newMin;
    }, []);

    const updateSliderStyle = useCallback(() => {
        if (
            !isMounted.current ||
            state.unlocked ||
            !sliderRef.current ||
            !sliderButtonRef.current ||
            !contentRef.current ||
            !containerRef.current
        ) {
            return;
        }

        const currentProgress = getCurrentSwipeProgress(0);

        // change the slider position on drag
        sliderRef.current.style.setProperty("--swipe-left-position", thisValue.current.sliderLeft + "px");

        // if > 70%, which we treat as success, complete all animations
        if (thisValue.current.sliderLeft > thisValue.current.containerWidth * SLIDE_THRESHOLD_PERCENT) {
            sliderRef.current.style.setProperty("--swipe-progress", "1");
            sliderRef.current.style.transform = `translateX(var(--swipe-left-position))`;
            sliderButtonRef.current.style.transform = `rotate(90deg)`;
        } else {
            // if less than 60
            if (thisValue.current.sliderLeft < thisValue.current.containerWidth * 0.6) {
                // reset all animations an orientations
                sliderRef.current.style.transform = `translateX(var(--swipe-left-position))`;
                sliderButtonRef.current.style.transform = `rotate(0deg)`;
                sliderRef.current.style.setProperty("--swipe-progress", "0");
            }

            // if between 60 to 70%, start  some of the animations
            else if (
                thisValue.current.sliderLeft >= thisValue.current.containerWidth * 0.6 &&
                thisValue.current.sliderLeft < thisValue.current.containerWidth * SLIDE_THRESHOLD_PERCENT
            ) {
                sliderRef.current.style.transform = `translateX(var(--swipe-left-position))`;
                sliderButtonRef.current.style.transform = `rotate(${Math.floor(
                    getValueAfterRangeConvert(0.6, 0.7, 0, 90, currentProgress),
                )}deg)`;

                sliderRef.current.style.setProperty(
                    "--swipe-progress",
                    String(getValueAfterRangeConvert(0.6, 0.7, 0, 1, currentProgress)),
                );
            }
        }

        // add a small transition when drag starts
        if (thisValue.current.sliderLeft) {
            containerRef.current.style.transform = "scale(0.985)";
        } else {
            containerRef.current.style.transform = "scale(1)";
        }

        // no transition while dragging
        sliderRef.current.style.transition = "";

        // When dragging change the opacity of the text shown
        // Math here basically is to convert the amount of drag to an opacity value between 0 to 1
        contentRef.current.style.opacity = String(1 - getCurrentSwipeProgress());

        props.onDragUpdate?.(thisValue.current.sliderLeft, thisValue.current.containerWidth);
    }, [getCurrentSwipeProgress, getValueAfterRangeConvert, isMounted, props, state.unlocked]);

    const resetStyles = useCallback(() => {
        if (!containerRef.current || !contentRef.current) {
            return;
        }
        // Reset all styles to original when drag is done
        containerRef.current.style.width = containerRef.current.clientWidth + "px";
        contentRef.current.style.opacity = "1";
        containerRef.current.style.transform = "scale(1)";
    }, []);

    const resetSliderButton = useCallback(() => {
        if (!sliderRef.current || !isMounted.current || !sliderButtonRef.current) return;

        sliderRef.current.style.setProperty("--swipe-progress", "0");
        updateSliderStyle();
        resetStyles();
        setState({
            unlocked: false,
        });
        sliderRef.current.style.transition = `transform 0.5s ease-out`;
        sliderRef.current.style.transform = `translateX(0px)`;
        sliderButtonRef.current.style.transform = `rotate(0deg)`;
    }, [isMounted, resetStyles, updateSliderStyle]);

    useImperativeHandle(props.sliderButtonRef, () => ({
        resetSliderButton: async (): Promise<void> => {
            resetSliderButton();
        },
    }));

    const onSuccess = useCallback(
        (sliderLeft: number, containerWidth: number) => {
            resetStyles();
            setState({
                unlocked: true,
            });
            props?.onSuccess?.(sliderLeft, containerWidth);
        },
        [props, resetStyles],
    );

    const onFailure = useCallback(
        (sliderLeft: number, containerWidth: number) => {
            resetStyles();
            setState({
                unlocked: false,
            });
            props?.onFailure?.(sliderLeft, containerWidth);
        },
        [props, resetStyles],
    );

    const onDrag = useCallback(
        event => {
            if (!isMounted.current || state.unlocked) return;
            const sliderVerticalPosition = isTouchEvent(event)
                ? (event as React.TouchEvent).touches[0].clientY
                : (event as React.MouseEvent).clientY;
            const verticalDifference = Math.abs(sliderVerticalPosition - thisValue.current.startY);

            if (thisValue.current.isDragging && verticalDifference < TOUCH_Y_THRESHOLD) {
                const sliderLeftPosition = isTouchEvent(event)
                    ? (event as React.TouchEvent).touches[0].clientX
                    : (event as React.MouseEvent).clientX;
                thisValue.current.sliderLeft = Math.min(
                    Math.max(0, sliderLeftPosition - thisValue.current.startX),
                    thisValue.current.containerWidth,
                );

                updateSliderStyle();
            }
        },
        [isMounted, state.unlocked, updateSliderStyle],
    );

    const stopDrag = useCallback(() => {
        if (!isMounted.current || state.unlocked || !sliderRef.current) return;

        if (thisValue.current.isDragging) {
            thisValue.current.isDragging = false;
            if (thisValue.current.sliderLeft > thisValue.current.containerWidth * SLIDE_THRESHOLD_PERCENT) {
                const valueSwiped = thisValue.current.sliderLeft;
                thisValue.current.sliderLeft = thisValue.current.containerWidth;
                updateSliderStyle();
                sliderRef.current.style.transition = "";
                onSuccess(valueSwiped, thisValue.current.containerWidth);
            } else {
                const valueSwiped = thisValue.current.sliderLeft;
                // reset the slider back to 0 with 0.5s transition
                thisValue.current.sliderLeft = 0;
                updateSliderStyle();
                sliderRef.current.style.transition = `transform 0.3s ease-out`;
                sliderRef.current.style.transform = `translateX(0px) rotate(0deg)`;
                onFailure(valueSwiped, thisValue.current.containerWidth);
            }
        }
    }, [isMounted, onFailure, onSuccess, state.unlocked, updateSliderStyle]);

    const startDrag = useCallback(
        (event: React.MouseEvent | React.TouchEvent) => {
            event.stopPropagation();

            if (!isMounted.current || state.unlocked || !sliderRef.current) return;

            thisValue.current.isDragging = true;
            if (isTouchEvent(event)) {
                thisValue.current.startX = (event as React.TouchEvent).touches[0].clientX;
                thisValue.current.startY = (event as React.TouchEvent).touches[0].clientY;
            } else {
                thisValue.current.startX = (event as React.MouseEvent).clientX;
                thisValue.current.startY = (event as React.MouseEvent).clientY;
            }
        },
        [isMounted, state.unlocked],
    );

    // this handler is on MouseDown so that we can stop propagation of the event from startDrag
    // otherwise the ux would look broken
    const showSubtleSlideBump = useCallback(() => {
        if (!sliderRef.current || thisValue.current.isDragging || thisValue.current.sliderLeft) return;
        setShowTapAnimation(true);

        setTimeout(() => {
            if (!sliderRef.current) return;
            setShowTapAnimation(false);
        }, ANIMATION_DURATION);
    }, []);

    useEffectOnce(() => {
        if (isTouchDevice) {
            document.addEventListener("touchmove", onDrag);
            document.addEventListener("touchend", stopDrag);
        } else {
            document.addEventListener("mousemove", onDrag);
            document.addEventListener("mouseup", stopDrag);
        }
        if (containerRef?.current) {
            // this is the swipable width for the button
            thisValue.current.containerWidth = containerRef.current.clientWidth - ICON_WIDTH - CONTAINER_PADDING * 2;
        }
    });

    const amountDetailsUi: ReactNode = useMemo(() => {
        if (props.turboCtaText != null) {
            return <>{props.turboCtaText}</>;
        }
        return (
            <>
                {`${props.prefixText} | `}
                {props.discountSubtext && props.discountSubtext !== props.amountText && (
                    <DiscountSubText>
                        <Text as="span" variant="V2.body1.neutral.regular" color="text_inverse_Med_Emphasis">
                            {props.discountSubtext}
                        </Text>
                    </DiscountSubText>
                )}
                {props.amountText}
            </>
        );
    }, [props.amountText, props.discountSubtext, props.prefixText, props.turboCtaText]);

    return (
        <Container
            data-testid={props.dataTestId}
            className={props.containerClassName}
            ref={containerRef}
            onMouseDown={showSubtleSlideBump}
        >
            <Slider
                ref={sliderRef}
                onMouseDown={startDrag}
                onTouchStart={startDrag}
                $isLarge={props.increaseSliderWidth}
                data-testid={DATA_TEST_IDS.SLIDER}
            >
                <SwipeButton
                    $showInitialAnimation={props.showInitialBumpAnimation}
                    $showTapAnimation={showTapAnimation}
                    ref={sliderButtonRef}
                >
                    <IconContainer>
                        <CircleIcon />
                    </IconContainer>
                </SwipeButton>
            </Slider>
            <Content>
                <Text as="span" ref={contentRef} variant="V2.subheading1.neutral.bold" color="text_Button_Primary">
                    {state.unlocked ? props.successText || amountDetailsUi : amountDetailsUi}
                </Text>
                {props.showSubtextForInstantFoP && (
                    <SubTextContainer>
                        <BoltIcon />
                        <SubText>
                            <Text variant="V2.body3.neutral.regular" color="text_inverse_High_Emphasis">
                                {PAY_INSTANTLY_SUBTEXT_TURBO_WIDGET}
                            </Text>
                        </SubText>
                    </SubTextContainer>
                )}
            </Content>
        </Container>
    );
}

export default memo(SlideButton);
